import React, { useEffect, useState } from "react";
import { getFeedObserver } from "@/components/vault/feedObserverSingleton";
import { VAULT_OBJECT_THUMBNAIL_TOP_PERCENT } from "@/components/vault/vault-object-util";

interface FeedObjectLazyImageProps {
  src: string;
  name: string;
  observeTargetRef: React.MutableRefObject<HTMLAnchorElement | null>;
  hover: boolean;
  setImageError: (error: boolean) => void;
}

export const FeedObjectLazyImage = React.memo(
  ({
    src,
    name,
    observeTargetRef,
    hover,
    setImageError,
  }: FeedObjectLazyImageProps) => {
    const [inView, setInView] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
      const el = observeTargetRef.current;
      if (!el) return;

      const handleIntersect = (entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
          setInView(true);
          getFeedObserver()?.unobserve(el);
        }
      };

      (el as any).__onIntersect = handleIntersect;
      getFeedObserver()?.observe(el);

      return () => {
        delete (el as any).__onIntersect;
        getFeedObserver()?.unobserve(el);
      };
    }, [observeTargetRef]);

    const imgSrc = inView
      ? src
      : "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

    if (error) return null;
    return (
      <img
        src={imgSrc}
        alt={name}
        loading={inView ? "lazy" : undefined}
        onLoad={() => setLoaded(true)}
        onErrorCapture={() => {
          setError(true);
          setImageError(true);
        }}
        style={{
          visibility: loaded ? "visible" : "hidden",
          width: "100%",
          height: "auto",
          objectFit: "contain",
          transformOrigin: `50% ${VAULT_OBJECT_THUMBNAIL_TOP_PERCENT}%`,
          transition: "transform 0.2s ease-in-out",
          transform: `scale(${inView && hover ? 2.2 : 2.0})`,
        }}
      />
    );
  },
);

FeedObjectLazyImage.displayName = "FeedObjectLazyImage";
